import { memo } from "react";

import { cn } from "@/lib/chillnn/cn";

import type { ForNodeType } from "@/components/features/_model/types";

import { Switch } from "@/components/ui/button/switch";
import CheckCircleIcon from "@/components/ui/icon/check-circle";
import WarningInfoIcon from "@/components/ui/icon/warning-info";
import { Label } from "@/components/ui/label/label";

const STATUS_TEXT = {
	private: "予約ページで表示・販売されていません",
	public: "予約ページで表示・販売されています",
} as const;

const PANEL_TRANSITION =
	"transition-[background-color,border-color,color] duration-200 ease-[cubic-bezier(0.23,1,0.32,1)] motion-reduce:transition-none";

const STATUS_FADE_TRANSITION = "transition-opacity duration-150 ease-out motion-reduce:transition-none";

type Props = {
	inSale: ForNodeType<boolean>;
	onChange: (input: boolean | undefined) => void;
};

const StatusMessageIcon = memo(({ isPrivate }: { isPrivate: boolean }) => {
	return (
		<span aria-hidden className="grid size-5 shrink-0 place-items-center">
			<WarningInfoIcon
				className={cn(
					"col-start-1 row-start-1 text-pink-600",
					STATUS_FADE_TRANSITION,
					isPrivate ? "opacity-100" : "pointer-events-none opacity-0",
				)}
				size="sm"
			/>
			<CheckCircleIcon
				className={cn(
					"col-start-1 row-start-1 text-green-600",
					STATUS_FADE_TRANSITION,
					isPrivate ? "pointer-events-none opacity-0" : "opacity-100",
				)}
				size="sm"
			/>
		</span>
	);
});
StatusMessageIcon.displayName = "StatusMessageIcon";

const StatusMessageText = memo(({ isPrivate }: { isPrivate: boolean }) => {
	return (
		<span className="inline-grid">
			<span
				className={cn(
					"col-start-1 row-start-1 whitespace-nowrap leading-none",
					STATUS_FADE_TRANSITION,
					isPrivate ? "opacity-100" : "pointer-events-none opacity-0",
				)}
			>
				{STATUS_TEXT.private}
			</span>
			<span
				className={cn(
					"col-start-1 row-start-1 whitespace-nowrap leading-none",
					STATUS_FADE_TRANSITION,
					isPrivate ? "pointer-events-none opacity-0" : "opacity-100",
				)}
			>
				{STATUS_TEXT.public}
			</span>
		</span>
	);
});
StatusMessageText.displayName = "StatusMessageText";

/**
 * 商品編集ヘッダー用の公開/非公開コントロール。
 * モバイルは [Label × 2] [スイッチ] のみ。laptop 以上でアイコンと状態文言を表示する。
 */
export const InSaleStatusControl = memo(({ inSale, onChange }: Props) => {
	const isPublic = inSale.value;
	const isPrivate = !isPublic;

	return (
		<div
			className={cn(
				"flex items-center gap-x-2 rounded-lg border px-2.5 py-2 laptop:gap-x-2.5 laptop:px-3",
				PANEL_TRANSITION,
				isPrivate ? "border-pink-200 bg-pink-200/40" : "border-green-200 bg-green-200/25",
			)}
			role={isPrivate ? "alert" : "status"}
			aria-live="polite"
		>
			<div className="flex shrink-0 items-center gap-x-1">
				<Label variant={isPrivate ? "pink" : "black"} text="非公開" size="sm" />
				<Label variant={isPublic ? "green" : "black"} text="公開" size="sm" />
			</div>

			<Switch
				aria-label="公開状態を切り替える"
				className="mt-0 shrink-0"
				checked={isPublic}
				onCheckedChange={onChange}
			/>

			<div
				className={cn(
					"hidden min-w-0 items-center gap-x-1.5 text-xs font-semibold tracking-wide laptop:flex",
					PANEL_TRANSITION,
					isPrivate ? "text-pink-600" : "text-green-600",
				)}
			>
				<StatusMessageIcon isPrivate={isPrivate} />
				<StatusMessageText isPrivate={isPrivate} />
			</div>
		</div>
	);
});
InSaleStatusControl.displayName = "InSaleStatusControl";
